/**
 * The Gateways tab (user, 19/10/2026: "a gateway overview — what a client
 * who automates everything needs to see"): the country's gateways today in
 * one card — how many send, how many are blocked, who won the route test,
 * when the verdicts last judged — then the table of gateways, each route
 * test's results, and the blocks.
 * Degrades honestly: a day not generated yet shows the plan's gateways with
 * no sends, a failed read says so and offers Retry.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { RadioTower } from 'lucide-react';
import type { DayPlanPreview, DropGenErrorRow, DropGenGatewayOverview, DropGenGatewayTrends } from '@trafficpulse/shared';

import { Badge, Card, HintDot } from '@/components/ui';
import { Settings2 } from 'lucide-react';

import { instantText } from '../../utils/clock/dk-label';
import { useCountryClock } from '../shared/time/country-clock-context';
import { SectionAction } from '../timeline/section/SectionAction';
import { BlocksSection } from './blocks/BlocksSection';
import { ExperimentsSection } from './experiments/ExperimentsSection';
import { SendFailuresSection } from './failures/SendFailuresSection';
import { TrendsSection } from './trends/TrendsSection';
import { type GatewayToday, gatewaysToday, pastLimits } from './gateways-today';
import { RouteTestSection } from './route-test/RouteTestSection';
import { GatewaysTable } from './table/GatewaysTable';

const HINT =
  'Every gateway the day touches: the drops and SMS planned and sent through it, today’s real delivered %, bot % and CR from the sent drops (read when the verdicts are evaluated), the estimated cost, and what the automation decided — a block, a route-test win. Rates in amber are past the usual verdict limits (delivered under 85 %, bot clicks over 15 %).';

interface Props {
  preview: DayPlanPreview | undefined;
  overview: DropGenGatewayOverview | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  scopeIds?: ReadonlySet<string> | null;
  scopeLabel?: string | null;
  /** Opens a container (the route test) in the editor. */
  onEditContainer?: (containerId: string) => void;
  /** Opens the open group's master config on its Gateways tab. */
  onOpenMasterGateways?: () => void;
  /** The country, for the experiments read; absent in tests that have none. */
  countryKey?: string;
  /** The last days per gateway, with the range the page asked for. */
  trends?: { data: DropGenGatewayTrends | undefined; days: number; onDays: (days: number) => void; loading: boolean };
  /** Today's error rows (19/10/2026): the refused sends mark their gateway "send failing". */
  errors?: DropGenErrorRow[];
  onOpenErrors?: () => void;
}

/** Before the day is generated, the plan preview knows how many test drops it lays. */
const plannedInPreview = (preview: DayPlanPreview | undefined, containerId: string): number =>
  preview?.containers.find((c) => c.containerId === containerId)?.drops.filter((d) => !d.runtime?.tombstoned).length ?? 0;

/** "4 gateways send today · 2 ok · 1 send failing · 1 past limits · 1 blocked · winner 23Telecom (binom)". */
export const headline = (rows: GatewayToday[]): string => {
  const sending = rows.filter((g) => g.planned.drops > 0).length;
  const blocked = rows.filter((g) => g.status === 'blocked').length;
  const failing = rows.filter((g) => g.status === 'failing').length;
  const past = rows.filter((g) => g.status !== 'blocked' && g.status !== 'failing' && pastLimits(g.rates)).length;
  const ok = rows.filter((g) => g.planned.drops > 0 && g.status !== 'blocked' && g.status !== 'failing' && !pastLimits(g.rates)).length;
  const winners = rows.flatMap((g) => g.winnerFor.map((w) => `${g.name.replace(/\s*\([^)]*\)\s*$/, '')} (${w.masterName})`));
  return [
    `${sending} gateway${sending === 1 ? '' : 's'} send${sending === 1 ? 's' : ''} today`,
    sending > 0 ? `${ok} ok` : null,
    failing > 0 ? `${failing} send failing` : null,
    past > 0 ? `${past} past limits` : null,
    blocked > 0 ? `${blocked} blocked` : null,
    winners.length > 0 ? `winner ${winners.join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const GatewaysTabCard: FC<Props> = ({ preview, overview, loading, failed, onRetry, scopeIds = null, scopeLabel = null, onEditContainer, onOpenMasterGateways, countryKey, trends, errors = [], onOpenErrors }) => {
  const colorOf = (containerId: string) => preview?.containers.find((c) => c.containerId === containerId)?.color ?? '#64748b';
  const clock = useCountryClock();
  const rows = gatewaysToday(preview, overview, scopeIds, errors);
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-border/40 px-5 py-3">
        <RadioTower className="h-4 w-4 text-primary" aria-hidden />
        <span className="text-sm font-semibold">Gateways{overview ? ` — ${overview.runDate.slice(8, 10)}/${overview.runDate.slice(5, 7)}` : ''}</span>
        <HintDot label={HINT} />
        {overview && !overview.generated ? <Badge tone="secondary">not generated yet</Badge> : null}
        <span className="ml-auto font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {rows.length > 0 ? headline(rows) : ''}
          {scopeLabel ? ` · ${scopeLabel} only` : ''}
        </span>
        {onOpenMasterGateways ? (
          <SectionAction icon={Settings2} onClick={onOpenMasterGateways} title="The group's master config, on its Gateways tab: the pool, the strategy, the gateway verdict">
            Gateway settings
          </SectionAction>
        ) : null}
      </div>
      {failed && !overview ? (
        <div className="flex flex-col items-start gap-2 p-6">
          <p className="text-sm text-muted-foreground">gateway overview unavailable</p>
          <SectionAction onClick={onRetry}>Retry</SectionAction>
        </div>
      ) : loading && !preview ? (
        <div className="flex flex-col gap-1.5 p-5">
          {[0, 1, 2].map((i) => <div key={i} className="h-9 animate-pulse rounded-md bg-muted/60" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-start gap-1 p-6">
          <p className="text-sm font-medium">No gateway in use today.</p>
          <p className="max-w-xl text-sm text-muted-foreground">No planned drop has a gateway — add gateways to the master config (container editor › Gateways) and the day’s drops will go through them.</p>
        </div>
      ) : (
        <>
          <GatewaysTable gateways={rows} />
          <SendFailuresSection gateways={rows.filter((g) => g.failures.length > 0)} colorOf={colorOf} onOpenErrors={onOpenErrors} />
          {(overview?.routeTests ?? [])
            .filter((t) => !scopeIds || scopeIds.has(t.containerId) || scopeIds.has(t.masterId))
            .map((t) => (
              <RouteTestSection
                key={t.containerId}
                test={t}
                plannedInPreview={plannedInPreview(preview, t.containerId)}
                onEdit={onEditContainer ? () => onEditContainer(t.containerId) : undefined}
                onEditMaster={onOpenMasterGateways}
              />
            ))}
          <BlocksSection blocks={overview?.blocks.filter((b) => !scopeIds || scopeIds.has(b.containerId)) ?? []} />
          {countryKey ? <ExperimentsSection countryKey={countryKey} colorOf={colorOf} scopeIds={scopeIds} /> : null}
          {trends ? <TrendsSection trends={trends.data} days={trends.days} onDays={trends.onDays} loading={trends.loading} /> : null}
          <p className="border-t border-border/40 px-5 py-2.5 text-[11px] text-muted-foreground">
            {overview?.evaluatedAt
              ? `Rates read at the last verdict pass, ${instantText(overview.evaluatedAt, clock)}. Evaluate verdicts on the Live tab to refresh them.`
              : overview?.generated
                ? 'Rates appear once the verdicts are evaluated (Live tab › Evaluate verdicts) after the first sends.'
                : 'Sent counts, rates and blocks appear once the day is generated and sends go out.'}
          </p>
        </>
      )}
    </Card>
  );
};
